import type { Personality } from '../core/characters';
import { PRIM_CAPSULE, PRIM_DISK, type Primitive } from './sdf/primitives';

export type FacePose = {
  x: number; y: number; angle?: number; aimX: number; aimY: number;
  headRadius?: number;
  personality?: Personality; blink?: boolean; hurt?: boolean; stunned?: boolean;
  surprised?: boolean; focused?: boolean; happy?: boolean; dead?: boolean;
};

/** Brows and mouth stay chunky enough to read on a 30-pixel fighter. */
export function faceShapes(p: FacePose): Primitive[] {
  const out: Primitive[] = [];
  const scale = (p.headRadius ?? 0.36) / 0.36;
  const c = Math.cos(p.angle ?? 0), s = Math.sin(p.angle ?? 0);
  const point = (x: number, y: number) => ({ x: p.x + (x * c - y * s) * scale, y: p.y + (x * s + y * c) * scale });
  const line = (ax: number, ay: number, bx: number, by: number, r = 0.022) => {
    const a = point(ax, ay), b = point(bx, by);
    out.push({ kind: PRIM_CAPSULE, ax: a.x, ay: a.y, bx: b.x, by: b.y, r: r * scale });
  };
  const dot = (x: number, y: number, r: number) => { const a = point(x, y); out.push({ kind: PRIM_DISK, ax: a.x, ay: a.y, bx: a.x, by: a.y, r: r * scale }); };
  const ex = p.dead ? 0 : Math.max(-1, Math.min(1, p.aimX)) * 0.12;
  const ey = 0.035 + (p.dead ? 0 : Math.max(-0.7, Math.min(0.7, p.aimY)) * 0.09);
  const spread = 0.092 - Math.abs(ex) * 0.13;
  const sleepy = p.personality === 'sleepy' && !p.focused && !p.hurt && !p.surprised && !p.happy;
  for (const side of [-1, 1]) {
    const x = ex + side * spread;
    if (p.dead || p.stunned) {
      line(x - 0.035, ey - 0.028, x + 0.035, ey + 0.028);
      line(x - 0.035, ey + 0.028, x + 0.035, ey - 0.028);
    } else if (p.blink || p.hurt || sleepy) line(x - 0.036, ey, x + 0.036, ey + (p.hurt ? side * 0.023 : -0.009));
    else dot(x, ey, p.surprised ? 0.062 : 0.05);
    if (!p.dead && !p.stunned && !p.blink) {
      const brow = p.focused || p.personality === 'scrappy' ? -side * 0.032 : p.surprised || p.personality === 'nervous' ? side * 0.023 : 0.009;
      line(x - 0.045, ey + 0.105 + brow, x + 0.045, ey + 0.105 - brow, 0.018);
    }
  }
  const mouthY = ey - 0.14;
  if (p.surprised || p.stunned) dot(ex + 0.01, mouthY, 0.06);
  else if (p.happy) { line(ex - 0.07, mouthY + 0.025, ex, mouthY - 0.02); line(ex, mouthY - 0.02, ex + 0.075, mouthY + 0.03); }
  else if (p.hurt || p.dead || p.personality === 'nervous') line(ex - 0.045, mouthY, ex + 0.055, mouthY + 0.018);
  else if (sleepy) line(ex - 0.03, mouthY - 0.008, ex + 0.04, mouthY - 0.008);
  else line(ex - 0.02, mouthY, ex + 0.06, mouthY + (p.personality === 'confident' ? 0.03 : 0.005));
  return out;
}
